import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Check } from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Register() {
    const navigate = useNavigate();
    const { register } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);

    const handleGoogleSuccess = async (credentialResponse) => {
        try {
            const { data } = await axios.post(`${API}/auth/google`, {
                token: credentialResponse.credential
            });

            // 1. Save Token
            localStorage.setItem('subhack_token', data.access_token);

            // 2. Check for Stashed Guest Work
            const returnUrl = localStorage.getItem('subhack_return_url');

            // 3. Smart Redirect
            if (returnUrl) {
                // If we have saved work, go there (force reload to clear state)
                window.location.href = returnUrl;
            } else {
                // Default behavior for normal signups
                window.location.href = '/dashboard';
            }
        } catch (error) {
            console.error(error);
            toast.error('Google Sign-Up failed');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const returnUrl = localStorage.getItem('subhack_return_url');

        if (password !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }
        if (password.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }
        setLoading(true);
        try {
            await register(email, password);
            toast.success('Account created! Welcome to SubHack!');
            if (returnUrl) {
                window.location.href = returnUrl;
            } else {
                // Default fallback
                navigate('/dashboard');
            }
        } catch (error) {
            const message = error.response?.data?.detail || 'Registration failed.';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    const benefits = [
        { text: 'One active project at a time', highlight: false },
        { text: 'Access to featured clips', highlight: false },
        { text: 'Share your creation with a unique link', highlight: false },
        { text: 'Track your view count', highlight: false },
        // The highlighted item:
        { text: 'Upgrade after to access full features!', highlight: true }
    ];

    return (
        <div className="min-h-screen flex flex-col bg-background">
            <div className="p-4">
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" />
                    <span className="font-heading text-sm uppercase tracking-wider">Back to Home</span>
                </Link>
            </div>

            <div className="flex-1 flex items-center justify-center px-4 py-8">
                <div className="w-full max-w-md">
                    <div className="text-center mb-8">
                        <Link to="/">
                            <span className="font-brand text-4xl text-primary">SUBHACK</span>
                        </Link>
                        <h1 className="font-heading text-2xl uppercase mt-6 mb-2">Create Account</h1>
                        <p className="text-muted-foreground text-sm">
                            Start creating viral content today
                        </p>
                    </div>

                    <div className="card-base p-8">
                        {/* RECOMMENDED BADGE */}

                        {/* Benefits */}
                        <div className="mb-6 p-4 bg-primary/5 rounded-lg border border-primary/20">
                            <p className="font-heading text-xs uppercase tracking-wider text-primary mb-3">
                                Free account includes:
                            </p>
                            <ul className="space-y-2">
                                {benefits.map((benefit, idx) => (
                                    <li
                                        key={idx}
                                        className={`flex items-center gap-2 text-sm transition-colors duration-300 ${benefit.highlight
                                                /* IF HIGHLIGHTED: Melon Orange, Bold, Uppercase, slightly pulsing */
                                                ? "text-[#FF9E70] font-bold font-heading uppercase tracking-wide"
                                                /* ELSE: Standard muted text */
                                                : "text-muted-foreground"
                                            }`}
                                    >
                                        {/* ICON: Checks if highlighted to match the color, otherwise standard primary */}
                                        <Check className={`w-4 h-4 flex-shrink-0 ${benefit.highlight ? "text-[#FF9E70]" : "text-primary"
                                            }`} />

                                        {/* TEXT: Accessed via benefit.text now */}
                                        {benefit.text}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="flex justify-center -mb-1 relative z-10">
                            <span className="bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-widest py-0.5 px-3 rounded-full shadow-sm">
                                Recommended
                            </span>
                        </div>

                        {/* GOOGLE BUTTON with Hover Lift */}
                        <div className="mb-8 flex justify-center transform transition-transform duration-200 hover:scale-[1.02]">
                            <div className="shadow-lg rounded-full overflow-hidden border border-border/50 bg-white">
                                <GoogleLogin
                                    onSuccess={handleGoogleSuccess}
                                    onError={() => toast.error('Google Sign-In Failed')}
                                    theme="filled_black"
                                    shape="pill"
                                    width="360"
                                />
                            </div>
                        </div>

                        <div className="relative mb-6">
                            <div className="absolute inset-0 flex items-center">
                                <span className="w-full border-t border-border" />
                            </div>
                            <div className="relative flex justify-center text-xs uppercase">
                                <span className="bg-card px-2 text-muted-foreground">Or sign up with email</span>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="space-y-2">
                                <Label htmlFor="email" className="font-heading text-sm uppercase tracking-wider">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="you@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="input-base h-12"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="password" className="font-heading text-sm uppercase tracking-wider">Password</Label>
                                <Input
                                    id="password"
                                    type="password"
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    minLength={6}
                                    className="input-base h-12"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword" className="font-heading text-sm uppercase tracking-wider">Confirm Password</Label>
                                <Input
                                    id="confirmPassword"
                                    type="password"
                                    placeholder="••••••••"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    className="input-base h-12"
                                />
                            </div>

                            <Button type="submit" className="btn-primary w-full h-12" disabled={loading}>
                                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : 'Create Account'}
                            </Button>
                        </form>

                        <p className="text-xs text-muted-foreground text-center mt-4">
                            By creating an account, you agree to our{' '}
                            <Link to="/terms" className="text-primary hover:underline">Terms</Link>
                            {' '}and{' '}
                            <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
                        </p>
                    </div>

                    <p className="text-center mt-6 text-sm text-muted-foreground">
                        Already have an account?{' '}
                        <Link to="/login" className="text-primary hover:underline">Sign in</Link>
                    </p>
                </div>
            </div>
        </div>
    );
}